import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Fuse from 'fuse.js';
import AuthContext from '../contexts/AuthContext';
import api from '../utils/api';
import StatStrip from '../components/StatStrip';
import FilterRow from '../components/FilterRow';
import AccountsTable from '../components/AccountsTable';
import AddAccountForm from '../components/AddAccountForm';
import ImportModal from '../components/ImportModal';
import Modal from '../components/Modal';
import NotesModal from '../components/NotesModal';
import { cs2StatusView, CS2_SORT_RANK } from '../utils/cs2StatusView';
import { useToast } from '../components/Toast';

export default function AccountsPage() {
  const { role } = useContext(AuthContext);
  const { push: toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState(() => searchParams.get('steamId64') || '');
  useEffect(() => {
    if (searchParams.get('steamId64')) {
      const next = new URLSearchParams(searchParams);
      next.delete('steamId64');
      setSearchParams(next, { replace: true });
    }
  }, []);

  const [banFilter, setBanFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [cs2Filter, setCs2Filter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [notesFor, setNotesFor] = useState(null);

  const fetchAccounts = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await api.get('/api/accounts');
      setAccounts(resp.data);
    } catch {
      setError('Failed to fetch accounts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); }, []);
  useEffect(() => { setCurrentPage(1); }, [search, banFilter, accountTypeFilter, cs2Filter, sortConfig, pageSize, accounts]);

  const filtered = useMemo(() => accounts.filter((acc) => {
    const lbs = acc.lastBanState || {};
    const isBanned = !!lbs.communityBanned
        || (lbs.numberOfVACBans || 0) > 0
        || (lbs.numberOfGameBans || 0) > 0
        || (lbs.economyBan && lbs.economyBan !== 'none');
    if (banFilter === 'banned' && !isBanned) return false;
    if (banFilter === 'unbanned' && isBanned) return false;

    const isCredentialed = acc.accountType === 'credentialed';
    if (role === 'manager' || role === 'user') {
      if (accountTypeFilter === 'credentialed' && !isCredentialed) return false;
      if (accountTypeFilter === 'monitored' && isCredentialed) return false;
    }

    if (cs2Filter !== 'all') {
      const view = cs2StatusView(acc);
      if (view.kind !== cs2Filter) return false;
    }
    return true;
  }), [accounts, banFilter, accountTypeFilter, cs2Filter, role]);

  const fuse = useMemo(() => {
    const keys = ['displayName', 'steamId64'];
    if (role === 'manager' || role === 'user') keys.push('username');
    return new Fuse(filtered, { keys, threshold: 0.15, ignoreLocation: true, isCaseSensitive: false });
  }, [filtered, role]);

  const searched = useMemo(() => {
    if (!search.trim()) return filtered;
    return fuse.search(search.trim()).map((r) => r.item);
  }, [fuse, search, filtered]);

  const sorted = useMemo(() => {
    if (!sortConfig.key) return searched;
    const arr = [...searched];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortConfig.key) {
        case 'displayName':
          av = (a.displayName || '').toLowerCase();
          bv = (b.displayName || '').toLowerCase();
          break;
        case 'bans':
          av = (a.lastBanState?.numberOfVACBans || 0) + (a.lastBanState?.numberOfGameBans || 0);
          bv = (b.lastBanState?.numberOfVACBans || 0) + (b.lastBanState?.numberOfGameBans || 0);
          break;
        case 'lastOnline':
          av = a.lastOnline ? new Date(a.lastOnline).getTime() : 0;
          bv = b.lastOnline ? new Date(b.lastOnline).getTime() : 0;
          break;
        case 'cs2LastPlayed':
          av = a.cs2State?.cs2LastPlayed ? new Date(a.cs2State.cs2LastPlayed).getTime() : 0;
          bv = b.cs2State?.cs2LastPlayed ? new Date(b.cs2State.cs2LastPlayed).getTime() : 0;
          break;
        case 'cs2Hours':
          // cs2PlaytimeMinutes of null/undefined means "unknown"; use -1 as the
          // sentinel so unknown rows sort above 0-minute rows (which are real —
          // owns CS2, never played) in ascending order, and below them in
          // descending. The date columns above use 0 for unknowns, but 0
          // cannot be used here because 0 minutes is a real value.
          av = a.cs2State?.cs2PlaytimeMinutes ?? -1;
          bv = b.cs2State?.cs2PlaytimeMinutes ?? -1;
          break;
        case 'lastCheckedAt':
          av = a.lastBanState?.lastCheckedAt ? new Date(a.lastBanState.lastCheckedAt).getTime() : 0;
          bv = b.lastBanState?.lastCheckedAt ? new Date(b.lastBanState.lastCheckedAt).getTime() : 0;
          break;
        case 'cs2Status': {
          av = cs2StatusView(a).sortRank;
          bv = cs2StatusView(b).sortRank;
          if (av === bv && av === CS2_SORT_RANK.cooldown) {
            av = cs2StatusView(a).expiresAtMs || 0;
            bv = cs2StatusView(b).expiresAtMs || 0;
          }
          break;
        }
        default:
          av = ''; bv = '';
      }
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [searched, sortConfig]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageAccounts = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const requestSort = (key) => {
    setSortConfig((prev) => prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    try {
      await api.delete(`/api/accounts/${id}`);
      toast({ kind: 'success', message: 'Account deleted.' });
      fetchAccounts();
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Delete failed.' });
    }
  };

  const handleUnquarantine = async (id) => {
    try {
      await api.post(`/api/accounts/${id}/cs2/unquarantine`);
      toast({ kind: 'success', message: 'Account unquarantined.' });
      fetchAccounts();
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Unquarantine failed.' });
    }
  };

  const [checkingIds, setCheckingIds] = useState(() => new Set());

  const handleCheckNow = async (acc) => {
    if (checkingIds.has(acc._id)) return;
    setCheckingIds((prev) => {
      const next = new Set(prev);
      next.add(acc._id);
      return next;
    });
    try {
      const resp = await api.post(`/api/accounts/${acc._id}/cs2/check`);
      const o = resp.data?.outcome || {};
      let msg = `Checked ${acc.displayName || acc.steamId64}: `;
      if (o.outcome === 'checked') {
        msg += o.inCooldown ? `in cooldown (${o.cooldownLevel})` : 'ready';
      } else if (o.outcome === 'skipped' && o.reason === 'playing_now') {
        msg += 'someone is playing, skipped';
      } else if (o.outcome === 'skipped' && o.reason === 'not_played_since_last_check') {
        msg += 'no new play, skipped';
      } else {
        msg += `skipped (${o.reason || 'unknown'})`;
      }
      toast({ kind: 'success', message: msg });
      fetchAccounts();
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Check failed.' });
    } finally {
      setCheckingIds((prev) => {
        const next = new Set(prev);
        next.delete(acc._id);
        return next;
      });
    }
  };

  const handleCopy = (what, value) => {
    if (!value) {
      toast({ kind: 'warn', message: `No ${what} to copy.` });
      return;
    }
    navigator.clipboard.writeText(value)
      .then(() => toast({ kind: 'success', message: `${what.charAt(0).toUpperCase() + what.slice(1)} copied.` }))
      .catch(() => toast({ kind: 'error', message: `Failed to copy ${what}.` }));
  };

  const handleImport = async (data) => {
    try {
      const resp = await api.post('/api/accounts/import', { data });
      toast({ kind: 'success', message: `Imported ${resp.data.imported} account${resp.data.imported === 1 ? '' : 's'}.` });
      fetchAccounts();
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Import failed.' });
      throw err;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight">Accounts</h1>
          <div className="text-xs text-surface-muted">
            {total} shown · {accounts.length} total
          </div>
        </div>
        {role === 'manager' && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowImport(true)}
              className="px-[14px] py-2 bg-transparent text-accent border border-surface-border text-[11px] font-semibold uppercase tracking-wider rounded-[2px]"
            >Import CSV</button>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="px-[14px] py-2 bg-accent text-surface-base border-0 text-[11px] font-bold uppercase tracking-wider rounded-[2px]"
            >+ New account</button>
          </div>
        )}
      </div>

      <StatStrip accounts={accounts} />

      <FilterRow
        search={search} onSearchChange={setSearch}
        banFilter={banFilter} onBanFilterChange={setBanFilter}
        accountTypeFilter={accountTypeFilter} onAccountTypeFilterChange={setAccountTypeFilter}
        cs2Filter={cs2Filter} onCs2FilterChange={setCs2Filter}
        pageSize={pageSize} onPageSizeChange={setPageSize}
        role={role}
      />

      {error && <div className="text-steam-danger text-sm mb-3">{error}</div>}
      {loading && <div className="text-surface-muted text-sm mb-3">Loading accounts…</div>}

      <AccountsTable
        accounts={pageAccounts}
        sortConfig={sortConfig}
        onSort={requestSort}
        onDelete={handleDelete}
        onUnquarantine={handleUnquarantine}
        onCopy={handleCopy}
        onOpenNotes={(acc) => setNotesFor(acc)}
        onCheckNow={handleCheckNow}
        checkingIds={checkingIds}
        role={role}
        filtersActive={
          search.trim() !== '' ||
          banFilter !== 'all' ||
          accountTypeFilter !== 'all' ||
          cs2Filter !== 'all'
        }
        onClearFilters={() => {
          setSearch('');
          setBanFilter('all');
          setAccountTypeFilter('all');
          setCs2Filter('all');
        }}
      />

      {totalPages > 1 && (
        <div className="mt-3 flex justify-between items-center text-[11px] text-surface-muted">
          <div>Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, total)} of {total}</div>
          <div className="flex gap-1">
            <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} className="px-2 py-0.5 bg-surface-base border border-surface-border disabled:opacity-40">‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, currentPage - 3), Math.max(0, currentPage - 3) + 5).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setCurrentPage(p)}
                className={`px-2 py-0.5 border ${p === currentPage ? 'bg-accent border-accent text-surface-base font-bold' : 'bg-surface-base border-surface-border text-surface-text'}`}
              >{p}</button>
            ))}
            <button type="button" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} className="px-2 py-0.5 bg-surface-base border border-surface-border disabled:opacity-40">›</button>
          </div>
        </div>
      )}

      {showAdd && (
        <Modal title="Add account" onClose={() => setShowAdd(false)}>
          <AddAccountForm onAdded={() => { fetchAccounts(); setShowAdd(false); toast({ kind: 'success', message: 'Account added.' }); }} />
        </Modal>
      )}
      {showImport && (
        <Modal title="Bulk import" onClose={() => setShowImport(false)}>
          <ImportModal onImport={handleImport} closeModal={() => setShowImport(false)} />
        </Modal>
      )}
      {notesFor && (
        <NotesModal
          account={notesFor}
          onClose={() => setNotesFor(null)}
          onMutated={fetchAccounts}
        />
      )}
    </div>
  );
}
